/**
 * Sticky Container Alpine.js Component
 * Handles desktop sticky stacking behavior for all screen sizes
 */

export default {
  name: 'stickyContainer',
  component(settings) {
    return {
      sectionId: settings.section_id,
    currentBlockIndex: 0,
    totalBlocks: 0,
    stickyOffsetObserver: null,
    selectorMutationObserver: null,
    currentStickyOffset: 0,
    intersectionObservers: [],
    
    init() {
      this.setupBlocks();
      this.setupDesktopSticky();
      this.setupResizeListener();
      this.setupStickyOffsetObserver();
      
      // Watch for changes in sticky offset selector
      this.watchStickyOffsetSelector();
      
      // Ensure proper initial state after DOM is ready
      this.$nextTick(() => {
        this.initializeBlockPositions();
      });
    },

    watchStickyOffsetSelector() {
      // Use MutationObserver to watch for changes in the data attributes
      if (window.MutationObserver) {
        this.selectorMutationObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && 
                mutation.attributeName === 'data-sticky-offset-selector') {
              this.setupStickyOffsetObserver();
              this.recalculatePositions();
            }
          });
        });
        
        const blocksContainer = this.$refs.blocksContainer;
        if (blocksContainer) {
          this.selectorMutationObserver.observe(blocksContainer, {
            attributes: true,
            attributeFilter: ['data-sticky-offset-selector']
          });
        }
      }
    },

    setupStickyOffsetObserver() {
      // Cleanup existing observer first
      if (this.stickyOffsetObserver) {
        this.stickyOffsetObserver.disconnect();
        this.stickyOffsetObserver = null;
      }
      
      const blocksContainer = this.$refs.blocksContainer;
      if (!blocksContainer) return;
      
      const offsetSelector = settings.sticky_offset_selector;
      if (!offsetSelector) return;
      
      try {
        const offsetElement = document.querySelector(offsetSelector);
        if (offsetElement && window.ResizeObserver) {
          this.stickyOffsetObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
              const newHeight = entry.contentRect.height;
              const previousHeight = this.currentStickyOffset;
              
              // Only recalculate if height actually changed
              if (newHeight !== previousHeight) {
                this.currentStickyOffset = newHeight;
                this.recalculatePositions();
              }
            }
          });
          
          this.stickyOffsetObserver.observe(offsetElement);
          
          // Set initial height and force initial calculation
          this.currentStickyOffset = offsetElement.offsetHeight;
          
          // Trigger initial position calculation
          this.$nextTick(() => {
            this.recalculatePositions();
          });
        }
      } catch (e) {
        console.warn('Error setting up sticky offset observer:', e);
      }
    },

    recalculatePositions() {
      const blocksContainer = this.$refs.blocksContainer;
      if (!blocksContainer) return;
      
      const blocks = Array.from(blocksContainer.querySelectorAll('.sticky-block-wrapper[data-block-type="sticky-item"]'));
      const stickyOffset = this.calculateStickyOffset();
      // Update desktop block positions
      blocks.forEach((block, index) => {
        block.style.top = `${stickyOffset}px`;
        block.style.zIndex = 10 + index;
      });
      
      // Update intersection observer root margins if they exist
      this.updateObserverMargins(stickyOffset);
    },

    updateObserverMargins(stickyOffset) {
      // Cleanup existing intersection observers
      this.cleanupIntersectionObservers();
      
      // This method helps update existing intersection observers with new root margins
      // when the sticky offset changes, ensuring proper detection thresholds
      const blocksContainer = this.$refs.blocksContainer;
      if (!blocksContainer) return;
      
      const blocks = Array.from(blocksContainer.querySelectorAll('.sticky-block-wrapper[data-block-type="sticky-item"]'));
      // Re-setup stacking observers with new margins
      this.setupStackingObserver(blocks, stickyOffset);
    },

    cleanupIntersectionObservers() {
      // Disconnect all existing intersection observers
      this.intersectionObservers.forEach(observer => {
        if (observer) {
          observer.disconnect();
        }
      });
      this.intersectionObservers = [];
    },

    initializeBlockPositions() {
      const blocksContainer = this.$refs.blocksContainer;
      if (!blocksContainer) return;
      
      const blocks = Array.from(blocksContainer.querySelectorAll('.sticky-block-wrapper[data-block-type="sticky-item"]'));
      const stickyOffset = this.calculateStickyOffset();
      
      // Ensure all blocks start with proper positioning
      blocks.forEach((block, index) => {
        block.style.zIndex = 10 + index;
        block.style.position = 'sticky';
        block.style.top = `${stickyOffset}px`;
        block.style.marginTop = '0';
      });
    },

    calculateStickyOffset() {
      const offsetSelector = settings.sticky_offset_selector;
      const fallbackOffset = parseInt(settings.sticky_offset) || 0;
      
      if (offsetSelector) {
        try {
          const offsetElement = document.querySelector(offsetSelector);
          if (offsetElement) {
            // Use offsetHeight for actual rendered height including padding and border
            const height = offsetElement.offsetHeight;
            return height;
          } else {
            console.warn(`Sticky offset selector "${offsetSelector}" did not match any element, using fallback: ${fallbackOffset}px`);
          }
        } catch (e) {
          console.warn('Invalid CSS selector for sticky offset:', offsetSelector, e);
        }
      }
      
      return fallbackOffset;
    },

    setupBlocks() {
      const blocksContainer = this.$refs.blocksContainer;
      if (!blocksContainer) return;

      // Get sticky-item blocks directly
      const blocks = Array.from(blocksContainer.querySelectorAll('.sticky-block-wrapper[data-block-type="sticky-item"]'));
      this.totalBlocks = blocks.length;
      
      if (this.totalBlocks === 0) {
        console.warn('No _sticky-item blocks found in container');
        return;
      }
      
      this.setupDesktopBlocks(blocks);
    },

    setupDesktopBlocks(blocks) {
      const blocksContainer = this.$refs.blocksContainer;
      if (!blocksContainer) return;

      const stickyOffset = this.calculateStickyOffset();

      // Apply sticky styling to each block
      blocks.forEach((block, index) => {
        block.dataset.blockIndex = index;
        block.style.width = 'auto';
        block.style.position = 'sticky';
        block.style.top = `${stickyOffset}px`;
        block.style.zIndex = 10 + index;
        
        // Remove any negative margins that cause overlap
        block.style.marginTop = '0';
      });
    },

    setupResizeListener() {
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          // Cleanup and re-setup in proper order
          this.setupStickyOffsetObserver(); // Setup observer first to get correct offset
          this.setupBlocks();
          this.setupDesktopSticky();
          this.initializeBlockPositions();
        }, 100);
      });
    },

    setupDesktopSticky() {
      const blocksContainer = this.$refs.blocksContainer;
      if (!blocksContainer) return;

      const blocks = Array.from(blocksContainer.querySelectorAll('.sticky-block-wrapper[data-block-type="sticky-item"]'));
      const stickyOffset = this.calculateStickyOffset();
      
      if (blocks.length === 0) return;

      // Clean up existing observers before setting up new ones
      this.cleanupIntersectionObservers();

      // Setup stacking observer to manage visibility and stacking
      this.setupStackingObserver(blocks, stickyOffset);
    },

    setupStackingObserver(blocks, stickyOffset) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const blockWrapper = entry.target;
          const blockIndex = parseInt(blockWrapper.dataset.blockIndex);
          
          if (entry.isIntersecting) {
            // When a block comes into view, ensure proper stacking
            this.handleStackingBlockVisible(blockIndex, blocks, stickyOffset);
          }
        });
      }, {
        threshold: [0, 0.1, 0.5],
        rootMargin: `${-stickyOffset}px 0px -50px 0px`
      });

      blocks.forEach(block => {
        observer.observe(block);
      });
      
      // Store observer for cleanup
      this.intersectionObservers.push(observer);
    },


    handleStackingBlockVisible(blockIndex, blocks, stickyOffset) {
      // In stacking mode, ensure all visible blocks maintain their sticky position
      // with proper z-index stacking (higher index = higher z-index)
      blocks.forEach((block, index) => {
        if (index <= blockIndex) {
          block.style.position = 'sticky';
          block.style.top = `${stickyOffset}px`;
          block.style.zIndex = 10 + index;
        }
      });
      
      this.currentBlockIndex = blockIndex;
    },


    // Cleanup method
    destroy() {
      // Remove event listeners and observers if needed
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      
      // Cleanup ResizeObserver
      if (this.stickyOffsetObserver) {
        this.stickyOffsetObserver.disconnect();
        this.stickyOffsetObserver = null;
      }
      
      // Cleanup MutationObserver
      if (this.selectorMutationObserver) {
        this.selectorMutationObserver.disconnect();
        this.selectorMutationObserver = null;
      }
      
      // Cleanup IntersectionObservers
      this.cleanupIntersectionObservers();
    }
    };
  }
};
